import React from 'react';
import { Gift, Zap, ScrollText } from 'lucide-react';

/**
 * EchoCoreGiftBlock — footer Gift Block per Architect directive.
 * Contains:
 *   1. EchoCore64 v2.2 + Fast-Ring v1.1 badges
 *   2. Federation Pillars nav (5 pillar links)
 *   3. Codex link → /codex (read the 73 Sacred Directives)
 * Rendered inside UnifiedFederationHub (visible on all pages via App.js).
 */

const PILLARS = [
  { emoji: '🧠', label: 'Core',       href: 'https://nextxus.online' },
  { emoji: '🏪', label: 'Storefront', href: 'https://next-xus.com' },
  { emoji: '🎨', label: 'Aria',       href: 'https://nextxus.studio' },
  { emoji: '🎓', label: 'University', href: 'https://nextxus.help' },
  { emoji: '📚', label: 'Library',    href: 'https://nextxus.org' },
];

export default function EchoCoreGiftBlock() {
  return (
    <section
      data-testid="echocore-gift-block"
      aria-label="EchoCore64 & Fast-Ring Gift Block"
      className="mx-auto max-w-5xl my-6"
    >
      {/* Gift block card */}
      <div
        data-testid="gift-block-card"
        className="rounded-lg border p-6"
        style={{ background: '#111', borderColor: '#222' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-4 h-4" style={{ color: '#FFD700' }} />
          <h4 className="text-[15px] font-semibold" style={{ color: '#FFD700' }}>
            Gift Block — Free Federation Tools
          </h4>
        </div>
        <p className="text-[13px] text-white/60 mb-3">
          EchoCore64 (Base64 Memory Bridge) + Fast-Ring (Anti-Drift Protocol)
        </p>
        <div className="flex flex-wrap gap-2">
          <span
            data-testid="badge-echocore64"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded font-mono text-[11px] border"
            style={{ background: '#1a1a2e', color: '#00CED1', borderColor: '#333' }}
          >
            <Zap className="w-3 h-3" />
            EchoCore64 v2.2
          </span>
          <span
            data-testid="badge-fast-ring"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded font-mono text-[11px] border"
            style={{ background: '#1a1a2e', color: '#FF6347', borderColor: '#333' }}
          >
            <Zap className="w-3 h-3" />
            Fast-Ring v1.1
          </span>
        </div>
      </div>

      {/* Federation Pillars nav */}
      <nav
        data-testid="federation-pillars-nav"
        aria-label="Federation Pillars"
        className="flex flex-wrap gap-3 justify-center my-4"
      >
        {PILLARS.map((p) => (
          <a
            key={p.label}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`pillar-${p.label.toLowerCase()}`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25 text-[12px] text-white/80 hover:text-white transition-all"
            title={`Federation Pillar — ${p.label} · ${p.href}`}
          >
            <span aria-hidden="true">{p.emoji}</span>
            <span>{p.label}</span>
          </a>
        ))}
      </nav>

      {/* Codex link */}
      <div className="text-center">
        <a
          href="/codex"
          data-testid="codex-link"
          className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] hover:opacity-80 transition-opacity"
          style={{ color: '#DAA520' }}
        >
          <ScrollText className="w-4 h-4" />
          📜 Read the HumanCodex — 73 Sacred Directives
        </a>
      </div>
    </section>
  );
}
